import { useEffect, useRef } from "react";

interface Props {
  overriding: boolean;
  reason: string;
  onOverriding: (overriding: boolean) => void;
  onReason: (reason: string) => void;
  /** The dropped PDF, opened in a new tab so the lines can be checked
   * against it. Null when no file is in hand. */
  pdfUrl?: string | null;
  /** Whose history keeps the reason: "quotation" by default. */
  record?: string;
  /** The hint in the reason box; the quotation's own wording by default. */
  placeholder?: string;
}

const QUOTATION_HINT = "For example: lines checked against the PDF; the supplier rounded its subtotal";

export function PdfLink({ url }: { url: string | null | undefined }) {
  if (!url) {
    return null;
  }
  return (
    <a className="go-link" href={url} target="_blank" rel="noopener noreferrer">
      View the PDF <span aria-hidden="true">↗</span>
      <span className="visually-hidden"> (opens in a new tab)</span>
    </a>
  );
}

/** Going on in spite of the totals check. The lines may still be right -- a
 * supplier rounding its subtotal, say -- and the office can see that against
 * the PDF. Overriding takes a reason, which the server keeps on the
 * quotation's history; until one is typed the drawer still holds.
 *
 * The control swaps a button for a text box and back, so focus follows it:
 * into the box when the override is chosen, back to the button when it is
 * withdrawn -- otherwise a keyboard user is dropped onto the page behind. */
export default function OverrideCheck({
  overriding,
  reason,
  onOverriding,
  onReason,
  pdfUrl = null,
  record = "quotation",
  placeholder = QUOTATION_HINT,
}: Props) {
  const box = useRef<HTMLTextAreaElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const was = useRef(overriding);

  useEffect(() => {
    if (overriding && !was.current) {
      box.current?.focus();
    } else if (!overriding && was.current) {
      button.current?.focus();
    }
    was.current = overriding;
  }, [overriding]);

  if (!overriding) {
    return (
      <div className="qr-override">
        <p className="cm-doc-note">If the lines agree with the PDF, you can override this check and say why.</p>
        <div className="qr-override-actions">
          <button ref={button} type="button" className="go-link" onClick={() => onOverriding(true)}>
            Override this check and continue
          </button>
          <PdfLink url={pdfUrl} />
        </div>
      </div>
    );
  }
  return (
    <div className="qr-override">
      <label className="move-note">
        Why is it safe to go on? (kept on the {record}&rsquo;s history)
        <textarea
          ref={box}
          value={reason}
          rows={2}
          required
          maxLength={500}
          placeholder={placeholder}
          onChange={(event) => onReason(event.target.value)}
        />
      </label>
      <div className="qr-override-actions">
        <button type="button" className="go-link" onClick={() => onOverriding(false)}>
          Withdraw the override
        </button>
        <PdfLink url={pdfUrl} />
      </div>
    </div>
  );
}
